import { type FC, useState, useMemo, useEffect, useRef, useCallback } from 'react';
import Hls from 'hls.js';
import { LIVE_CHANNELS, type LiveChannel, type VideoCategory } from '@/config/data-sources';

// ---------------------------------------------------------------------------
// Live Video News Panel
// Priority: HLS stream → YouTube embed → Website link
// Bug fixes: 0 retries (no flickering), single onError call (ref guard),
//            timeout-based fallback (8s), no manual play() calls
// ---------------------------------------------------------------------------

const CATEGORY_LABELS: Record<VideoCategory, string> = {
  global: 'Global',
  asia: 'Asia',
  mideast: 'M.East',
  europe: 'Europe',
  americas: 'Amer.',
  finance: 'Finance',
};
const CATEGORY_ORDER: VideoCategory[] = ['global', 'asia', 'mideast', 'europe', 'americas', 'finance'];

// ---------------------------------------------------------------------------
// HLS player — uses hls.js with zero retries (fail fast, no flickering)
// ---------------------------------------------------------------------------
const HlsPlayer: FC<{ url: string; onError: () => void }> = ({ url, onError }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const calledRef = useRef(false); // guard: onError fires at most once

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    calledRef.current = false;

    const fail = () => {
      if (!calledRef.current) {
        calledRef.current = true;
        onError();
      }
    };

    // 8-second timeout — if nothing plays, fallback
    const timer = setTimeout(fail, 8000);

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: false,
        // Zero retries everywhere → fail immediately on any error, no flickering
        manifestLoadingMaxRetry: 0,
        levelLoadingMaxRetry: 0,
        fragLoadingMaxRetry: 0,
        manifestLoadingTimeOut: 6000,
        levelLoadingTimeOut: 6000,
        fragLoadingTimeOut: 6000,
      });

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) { clearTimeout(timer); fail(); }
      });

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        clearTimeout(timer); // stream is live, cancel timeout
      });

      hls.loadSource(url);
      hls.attachMedia(video);

      return () => { clearTimeout(timer); hls.destroy(); };
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS
      video.src = url;
      video.onerror = () => { clearTimeout(timer); fail(); };
      video.oncanplay = () => clearTimeout(timer);
      return () => clearTimeout(timer);
    } else {
      clearTimeout(timer);
      fail();
    }
  }, [url, onError]);

  return (
    <video
      ref={videoRef}
      className="absolute inset-0 w-full h-full bg-black"
      muted
      autoPlay
      playsInline
      controls
    />
  );
};

// ---------------------------------------------------------------------------
// YouTube iframe (confirmed-working videoId)
// ---------------------------------------------------------------------------
const YouTubePlayer: FC<{ videoId: string }> = ({ videoId }) => (
  <iframe
    key={videoId}
    src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=1&modestbranding=1&rel=0`}
    className="absolute inset-0 w-full h-full"
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
    allowFullScreen
    loading="lazy"
  />
);

// ---------------------------------------------------------------------------
// Fallback: "Watch Live" button linking to channel website
// ---------------------------------------------------------------------------
const FallbackCard: FC<{ channel: LiveChannel }> = ({ channel }) => (
  <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900/90 gap-2">
    <div className="text-3xl">📡</div>
    <div className="text-[12px] font-semibold text-slate-200">{channel.name}</div>
    <div className="text-[10px] text-slate-500">Stream not embeddable</div>
    {channel.websiteUrl && (
      <a
        href={channel.websiteUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-2 px-4 py-1.5 text-[11px] font-medium bg-brand/20 hover:bg-brand/40 text-brand-light rounded-full transition-colors border border-brand/30"
      >
        Watch Live ↗
      </a>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Smart channel player — tries HLS → YouTube → Fallback
// ---------------------------------------------------------------------------
type PlayerState = 'hls' | 'youtube' | 'fallback';

const ChannelPlayer: FC<{ channel: LiveChannel }> = ({ channel }) => {
  const getInitial = (): PlayerState => {
    if (channel.hlsUrl) return 'hls';
    if (channel.videoId) return 'youtube';
    return 'fallback';
  };

  const [state, setState] = useState<PlayerState>(getInitial);

  // Reset when channel changes
  useEffect(() => { setState(getInitial()); }, [channel.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onHlsFail = useCallback(() => {
    setState(channel.videoId ? 'youtube' : 'fallback');
  }, [channel.videoId]);

  if (state === 'hls' && channel.hlsUrl) {
    return <HlsPlayer url={channel.hlsUrl} onError={onHlsFail} />;
  }
  if (state === 'youtube' && channel.videoId) {
    return <YouTubePlayer videoId={channel.videoId} />;
  }
  return <FallbackCard channel={channel} />;
};

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------
export const VideoNewsPanel: FC = () => {
  const [activeChannel, setActiveChannel] = useState<LiveChannel>(LIVE_CHANNELS[0]);
  const [categoryFilter, setCategoryFilter] = useState<VideoCategory | 'all'>('all');

  const filteredChannels = useMemo(
    () =>
      categoryFilter === 'all'
        ? LIVE_CHANNELS
        : LIVE_CHANNELS.filter((c) => c.category === categoryFilter),
    [categoryFilter],
  );

  const hasStream = (ch: LiveChannel) => !!(ch.hlsUrl || ch.videoId);

  return (
    <>
      {/* Header */}
      <div className="panel-header flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="panel-title">Live News</span>
          <span className="pulse-dot" />
          <span className="text-[10px] text-red-400 font-medium uppercase tracking-wider">Live</span>
        </div>
        <span className="text-[10px] text-slate-500">{LIVE_CHANNELS.length} channels</span>
      </div>

      {/* Video Player */}
      <div className="relative w-full h-[240px] bg-black rounded overflow-hidden">
        <ChannelPlayer key={activeChannel.id + activeChannel.name} channel={activeChannel} />
        {/* Channel badge */}
        <div className="absolute top-1.5 left-1.5 flex items-center gap-1.5 bg-black/70 backdrop-blur-sm rounded px-2 py-0.5 pointer-events-none z-10">
          <span className="text-[10px] font-bold text-white">{activeChannel.shortName}</span>
          {activeChannel.hlsUrl && (
            <span className="text-[8px] text-emerald-400 font-medium">HLS</span>
          )}
          {activeChannel.videoId && !activeChannel.hlsUrl && (
            <span className="text-[8px] text-red-400 font-medium">YT</span>
          )}
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex items-center gap-1 px-1 pt-1.5">
        {(['all', ...CATEGORY_ORDER] as const).map((cat) => (
          <button
            key={cat}
            onClick={() => setCategoryFilter(cat)}
            className={`px-2 py-0.5 text-[10px] rounded transition-colors capitalize ${
              categoryFilter === cat
                ? 'bg-brand/20 text-brand-light'
                : 'text-slate-500 hover:text-slate-300'
            }`}
          >
            {cat === 'all' ? 'All' : CATEGORY_LABELS[cat]}
          </button>
        ))}
      </div>

      {/* Channel Grid */}
      <div className="panel-body grid grid-cols-3 gap-1 max-h-28 overflow-y-auto pt-1">
        {filteredChannels.map((ch) => (
          <button
            key={ch.id + ch.name}
            onClick={() => setActiveChannel(ch)}
            className={`flex flex-col items-center gap-0.5 px-1.5 py-1.5 rounded text-center transition-all ${
              activeChannel.name === ch.name
                ? 'bg-brand/20 ring-1 ring-brand/40'
                : 'hover:bg-white/5'
            }`}
          >
            <div className="flex items-center gap-0.5">
              <span
                className={`text-[11px] font-bold leading-tight ${
                  activeChannel.name === ch.name ? 'text-brand-light' : 'text-slate-300'
                }`}
              >
                {ch.shortName}
              </span>
              {/* Green dot = HLS available, yellow = YouTube only, gray = link only */}
              <span className={`text-[7px] leading-none ${
                ch.hlsUrl ? 'text-emerald-500' : ch.videoId ? 'text-yellow-500' : 'text-slate-600'
              }`}>●</span>
            </div>
            <span className="text-[9px] text-slate-500 leading-tight truncate w-full">
              {ch.name.split(' ').slice(0, 2).join(' ')}
            </span>
          </button>
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 px-2 py-1 border-t border-slate-800/40">
        <span className="flex items-center gap-1 text-[8px] text-slate-600">
          <span className="text-emerald-500">●</span> HLS stream
        </span>
        <span className="flex items-center gap-1 text-[8px] text-slate-600">
          <span className="text-yellow-500">●</span> YouTube
        </span>
        <span className="flex items-center gap-1 text-[8px] text-slate-600">
          <span className="text-slate-600">●</span> Link only
        </span>
      </div>
    </>
  );
};
